import { addProjectKairoToRoom, createChatRoom } from "@/features/chat/api/chat-api";
import { chatQueries } from "@/features/chat/api/chat-keys";
import { containerQueries } from "@/features/containers/api/containers-keys";
import { docsQueries } from "@/features/docs/api/docs-keys";
import {
  type JiraSendInput,
  type JiraSendOutcome,
  fetchJiraLinks,
  sendToJira,
  syncJiraChildren,
} from "@/features/jira/api/jira-context-api";
import {
  type LinearSendInput,
  type LinearSendOutcome,
  fetchLinearLinks,
  sendToLinear,
  syncLinearChildren,
} from "@/features/linear/api/linear-context-api";
import {
  type NotionAttachOutcome,
  attachNotionPage,
} from "@/features/notion/api/notion-context-api";
import type { JiraChildrenSyncSummary } from "@shared/types/jira-context";
import type { LinearChildrenSyncSummary } from "@shared/types/linear-context";
import type {
  ChangeRequest,
  ChangeRequestUpdate,
  Container,
  CrClassification,
  CrNotionAttachMode,
  CrPriority,
  CrStatus,
} from "@shared/types/supabase.types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  type CrAssignableDocRole,
  changeRequestsApi,
  createCrEventsSubscription,
  removeCrEventsSubscription,
} from "./change-requests-api";
import { changeRequestsQueries } from "./change-requests-keys";
import { crSuggestionsQueries } from "./cr-suggestions-keys";

export function useChangeRequest(id: string | undefined) {
  return useQuery({
    ...changeRequestsQueries.byId(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useChangeRequests(projectId: string | undefined) {
  return useQuery({
    ...changeRequestsQueries.byProject(projectId!),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useSearchChangeRequests(query: string, projectId: string | undefined) {
  return useQuery({
    ...changeRequestsQueries.search(query, projectId!),
    enabled: !!projectId && query.length > 0,
    staleTime: 10 * 1000,
    // Keep the last results on screen while the next keystroke's search runs,
    // so the list does not flash "nothing found" between keys.
    placeholderData: keepPreviousData,
  });
}

/**
 * CR-148: resolve a typed CR number to the live CR of the project (null when
 * there is none — archived, other project or unknown all read the same).
 */
export function useResolveCrByNumber(projectId: string) {
  const queryClient = useQueryClient();
  return (sequentialId: number) =>
    queryClient.fetchQuery(changeRequestsQueries.byNumber(projectId, sequentialId));
}

export function useUpdateChangeRequest(crId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: ChangeRequestUpdate) => changeRequestsApi.updateChangeRequest(crId, patch),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byId(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byProject(data.project_container_id).queryKey,
      });
    },
  });
}

/**
 * CR-53: archive (soft-delete) a CR. Invalidates the board list so the card
 * disappears, and the CR's own detail query so a still-mounted detail page
 * falls into its not-found state instead of showing a stale archived CR.
 */
export function useArchiveChangeRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ crId }: { crId: string; projectId: string }) =>
      changeRequestsApi.archiveChangeRequest(crId),
    onSuccess: (_, { crId, projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byProject(projectId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byId(crId).queryKey,
      });
      // Search results feed the link-to-CR picker; a stale page could
      // otherwise offer the just-archived CR as a link target.
      void queryClient.invalidateQueries({
        queryKey: ["change-requests", "search", projectId],
      });
    },
  });
}

export function useUpdateChangeRequestStatus(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ crId, status }: { crId: string; status: CrStatus }) =>
      changeRequestsApi.updateChangeRequest(crId, { status }),
    onSuccess: (_, { crId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byProject(projectId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byId(crId).queryKey,
      });
    },
  });
}

export function useCreateChangeRequestFlow(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    ChangeRequest,
    Error,
    { name: string; classification: CrClassification; priority?: CrPriority }
  >({
    mutationFn: async ({ name, classification, priority }) => {
      // CR-27: creating a CR no longer auto-opens a chat. The CR is created on
      // its own; a chat is started opt-in from the CR detail page (useStartCrChat).
      const sequentialId = await changeRequestsApi.nextSequentialId(projectId);

      return changeRequestsApi.createChangeRequest({
        name,
        classification,
        projectId,
        originChatId: null,
        sequentialId,
        priority,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.byProject(projectId).queryKey,
      });
    },
  });
}

/**
 * Opt-in "Start chat with AI" for a CR (CR-27). Creates a chat linked to the CR
 * via containers.cr_id, adds Kairo, and returns the chat so the caller can
 * navigate into it. Replaces the old single auto-created origin chat with the
 * multi-chat cr_id model.
 */
export function useStartCrChat(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<Container, Error, { crId: string; chatName: string }>({
    mutationFn: async ({ crId, chatName }) => {
      // CR-42: CR-linked chats are shared with all project members. The DB
      // trigger (share_cr_linked_container) would set the flag at link time
      // anyway; creating the chat shared keeps the returned row and the query
      // cache truthful instead of one refetch behind.
      const chat = await createChatRoom(projectId, {
        name: chatName,
        is_shared: true,
      });

      await addProjectKairoToRoom(projectId, chat.id);

      await changeRequestsApi.linkChatToCr(chat.id, crId);
      return chat;
    },
    onSuccess: (_chat, { crId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedChats(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: chatQueries.roomsByParent(projectId).queryKey,
      });
    },
  });
}

/**
 * CR-84: "Create document" in the CR's Linked documents section. One RPC
 * creates the document already linked to the CR (see
 * changeRequestsApi.createCrLinkedDocument), and returns the full container so
 * the caller can navigate straight into it without waiting for any refetch.
 *
 * Two caches hold the new document. changeRequestsQueries.linkedDocs is the CR
 * detail page's "Linked documents" list.
 * docsQueries.byContainer is the project's document list behind the sidebar and
 * the Documents page, the same key useCreateDocument invalidates — keyed off
 * the parent the RPC actually inserted under (it derives it from the CR, so it
 * is authoritative), with `projectId` only as the fallback.
 *
 * Errors are deliberately not swallowed: the section renders them inline and
 * must not navigate.
 */
/**
 * CR-229 S9: "Use as Definition / Technical Plan" in the CR's linked documents.
 * The Jira send sources are derived from the linked list, so refreshing it is
 * all the page needs.
 */
export function useSetCrDocumentRole(crId: string) {
  const queryClient = useQueryClient();

  return useMutation<Container, Error, { documentId: string; role: CrAssignableDocRole | null }>({
    mutationFn: (input) => changeRequestsApi.setCrDocumentRole(input),
    // The role moves between documents, so every cache holding cr_role goes:
    // the CR's linked list (badges, Jira send sources), its event feed, and the
    // project's document lists (the Documents page's role filter).
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedDocs(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useCreateCrLinkedDocument(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<Container, Error, { crId: string; name: string }>({
    mutationFn: ({ crId, name }) => changeRequestsApi.createCrLinkedDocument({ crId, name }),
    onSuccess: (document, { crId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedDocs(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: docsQueries.byContainer(document.parent_container_id ?? projectId).queryKey,
      });
    },
  });
}

/**
 * CR-147: "Upload document" in the CR's Linked documents section.
 *
 * Unlike useCreateCrLinkedDocument it invalidates the CR's linked list ONLY:
 * an uploaded file is not a document, so it never appears in
 * `docsQueries.byContainer` (the sidebar and the Documents page) — invalidating
 * that key would just refetch a list the upload cannot have changed.
 *
 * Errors are deliberately not swallowed: the section maps the typed ones
 * (UnsupportedFileError, FileTooLargeError) to product copy.
 */
export function useUploadCrDocument() {
  const queryClient = useQueryClient();

  return useMutation<Container, Error, { crId: string; file: File }>({
    mutationFn: ({ crId, file }) => changeRequestsApi.uploadCrDocument({ crId, file }),
    onSuccess: (_container, { crId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedDocs(crId).queryKey,
      });
    },
  });
}

/**
 * CR-147: "Remove" on an uploaded file row.
 *
 * Same cache reasoning as useUploadCrDocument — a file only ever lived in the
 * CR's linked list. The error is not swallowed: the row renders it, because a
 * removal that failed must not read as a removal that worked.
 *
 * The event feed is invalidated too: the RPC writes a doc_unlinked event, and
 * unlike every other cr_events writer it is not a trigger on a row the feed's
 * realtime subscription watches (useCrEvents listens on cr_events itself, so
 * the INSERT does reach it — this invalidation is the belt to that braces, for
 * a client whose channel has dropped).
 */
export function useDeleteCrUpload() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { crId: string; containerId: string }>({
    mutationFn: ({ containerId }) => changeRequestsApi.deleteCrUploadedDocument(containerId),
    onSuccess: (_result, { crId }) => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedDocs(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}

/**
 * CR-147: hand the original bytes of an uploaded file back to the browser
 * through a short-lived signed URL. A mutation rather than a query because it
 * is an action with a side effect (a new URL is minted on every click) and
 * must never be cached: the URL expires after 60 s, so a cached one would
 * reopen as a 400. `isPending` / `isError` then drive the row's busy and error
 * states, which is why the component keeps no state of its own.
 */
export function useOpenCrUpload() {
  return useMutation<void, Error, { storagePath: string; fileName: string }>({
    mutationFn: async ({ storagePath, fileName }) => {
      const url = await changeRequestsApi.createCrUploadSignedUrl(storagePath, fileName);
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });
}

/**
 * CR-22: the CR's event log, kept live by a realtime subscription that
 * invalidates the query on every new event (INSERT, and since the CR-69
 * follow-up UPDATE too — see createCrEventsSubscription for why). Invalidating
 * rather than appending is what keeps the feed in the RPC's own order, which
 * CR-105 made newest-first.
 */
export function useCrEvents(crId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = createCrEventsSubscription(crId, () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
      // CR-148: dependency adds / removals and the done ↔ not-done trigger all
      // log on this CR (archiving a dependency does not — see the key), so the event channel doubles as the dependencies'
      // live refresh — no second realtime channel.
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.dependencies(crId).queryKey,
      });
      // CR-230: Notion references are not in realtime either; their add,
      // update and removal each log an event here.
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.notionReferences(crId).queryKey,
      });
      // CR-229: Jira links are not in realtime either; every send, sync and
      // Done transition logs an event here.
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.jiraLinks(crId).queryKey,
      });
      // CR-239: Linear links likewise (send, sync and completed transitions log here).
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linearLinks(crId).queryKey,
      });
      // CR-229 S9: a document's Definition / Technical Plan role (and its
      // link / unlink) is not in realtime either; each change logs an event
      // here, so another member's change or an agent's create_cr_document
      // role repaints the badges and the Jira send sources.
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linkedDocs(crId).queryKey,
      });
      // CR-235: a contributor's suggestion edit and file add / remove each log
      // an event here; cr_suggestions is not in realtime, so the member's
      // Suggestions section refreshes off this channel.
      void queryClient.invalidateQueries({
        queryKey: crSuggestionsQueries.byCr(crId).queryKey,
      });
    });
    return () => removeCrEventsSubscription(channel);
  }, [crId, queryClient]);

  return useQuery(changeRequestsQueries.events(crId));
}

/**
 * CR-148: the CR's dependency rows (active and history). Kept live by the
 * cr_events subscription in useCrEvents, which the CR page always mounts.
 */
export function useCrDependencies(crId: string) {
  return useQuery(changeRequestsQueries.dependencies(crId));
}

/**
 * CR-148: add / remove one dependency of `crId`. Both refresh the rows and the
 * event log directly (the realtime callback would too, but the editor should
 * not wait on the channel to show its own write). Refusals reach the caller as
 * the RPC error — map them with `crDependencyErrorReason`.
 */
function useCrDependencyMutation(
  crId: string,
  write: (crId: string, dependsOnId: string) => Promise<void>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dependsOnId: string) => write(crId, dependsOnId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.dependencies(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}

export function useAddCrDependency(crId: string) {
  return useCrDependencyMutation(crId, changeRequestsApi.addCrDependency);
}

export function useRemoveCrDependency(crId: string) {
  return useCrDependencyMutation(crId, changeRequestsApi.removeCrDependency);
}

/**
 * Which surface the shared linked-CR control is mounted on. The two surfaces do
 * not share a write path — see `changeRequestsApi.setChatCrId` for why a chat
 * cannot be a direct `containers` update (CR-149 D1/D3).
 *
 * The chat variant carries its `projectId` because its caches — unlike a
 * document's — are keyed by the parent project, and the surface is the only
 * thing the mutation is given.
 */
export type LinkedCrSurface =
  | { type: "document"; id: string }
  | { type: "chat"; id: string; projectId: string };

/**
 * Every cache that stops being true when `surface` changes the CR it points at.
 *
 * Kept as one exhaustive `switch` outside the mutation so both surfaces are
 * explicit cases — neither is the fall-through default — and so a third surface
 * fails to typecheck here instead of silently inheriting another's keys.
 *
 * CR-84: both the CR gained and the CR left list this container on their detail
 * page, and queries are stale only after 60 s (main.tsx). Without these, linking
 * here and opening the CR within the minute serves a cached list that is missing
 * the container — or, on unlink, still showing it.
 */
function linkedCrInvalidationKeys(
  surface: LinkedCrSurface,
  affectedCrIds: Iterable<string>,
): readonly unknown[][] {
  switch (surface.type) {
    case "chat":
      return [
        // The chat page reads its `cr_id` off the container row, and the
        // sidebar's room list carries the link too.
        [...containerQueries.byId(surface.id).queryKey],
        [...chatQueries.roomsByParent(surface.projectId).queryKey],
        ...[...affectedCrIds].map((id) => [...changeRequestsQueries.linkedChats(id).queryKey]),
      ];
    case "document":
      return [
        ["documents", surface.id],
        ...[...affectedCrIds].map((id) => [...changeRequestsQueries.linkedDocs(id).queryKey]),
      ];
  }
}

/**
 * Sets or clears the CR a document or chat is linked to (CR-149 D3/D8).
 *
 * One mutation that branches on the surface rather than one hook per surface,
 * so the shared control does not instantiate both surfaces' mutations on every
 * render. The write paths genuinely differ: a document is a direct `containers`
 * update under the document UPDATE policy, a chat goes through the
 * `set_chat_cr_id` RPC — see `changeRequestsApi.setChatCrId` for why.
 */
export function useSetLinkedCr(surface: LinkedCrSurface, previousCrId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (crId: string | null) =>
      surface.type === "chat"
        ? changeRequestsApi.setChatCrId(surface.id, crId)
        : changeRequestsApi.setDocCrId(surface.id, crId),
    // onSuccess and nothing else on purpose: a refused write must never repaint
    // the control with the CR it failed to move to, so there is no onSettled.
    onSuccess: (_data, crId) => {
      const affectedCrIds = new Set(
        [crId, previousCrId].filter((value): value is string => !!value),
      );
      for (const queryKey of linkedCrInvalidationKeys(surface, affectedCrIds)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** CR-230: the CR's Notion references (live through useCrEvents' callback). */
export function useCrNotionReferences(crId: string) {
  return useQuery(changeRequestsQueries.notionReferences(crId));
}

/** Refresh what a Notion attach / update / removal changed on this CR. */
function useInvalidateCrNotion(crId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: changeRequestsQueries.notionReferences(crId).queryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: changeRequestsQueries.events(crId).queryKey,
    });
  };
}

/**
 * CR-230: attach a Notion page to the CR through the api-server, or (mode
 * `update`) re-read it into the existing row. A `duplicate` outcome wrote
 * nothing, so it refreshes nothing either.
 */
export function useAttachNotionPage(crId: string, containerId: string) {
  const invalidate = useInvalidateCrNotion(crId);
  return useMutation<NotionAttachOutcome, Error, { pageId: string; mode: CrNotionAttachMode }>({
    mutationFn: ({ pageId, mode }) => attachNotionPage({ containerId, crId, pageId, mode }),
    onSuccess: (outcome) => {
      if (outcome.status !== "duplicate") {
        invalidate();
      }
    },
  });
}

/** CR-230 (Q4): remove one Notion reference of the CR. */
export function useRemoveCrNotionReference(crId: string) {
  const invalidate = useInvalidateCrNotion(crId);
  return useMutation<void, Error, string>({
    mutationFn: (referenceId) => changeRequestsApi.removeCrNotionReference(referenceId),
    onSuccess: invalidate,
  });
}

/** D6: a link's status is refreshed from Jira when its snapshot is older than this. */
const JIRA_STATUS_STALE_MS = 2 * 60 * 1000;

/**
 * CR-229: the CR's Jira links. The database read renders first (D6); then,
 * when the CR has links, the api-server is asked once per staleness window to
 * refresh their statuses from Jira, and a refreshed answer replaces the rows.
 * A failed refresh changes nothing (each link keeps its snapshot).
 */
export function useCrJiraLinks(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  const links = useQuery(changeRequestsQueries.jiraLinks(crId));
  const hasLinks = (links.data?.length ?? 0) > 0;
  useQuery({
    queryKey: changeRequestsQueries.jiraLinksRefreshKey(crId, projectId),
    queryFn: async () => {
      const answer = await fetchJiraLinks({ containerId: projectId, crId });
      if (answer.refreshed) {
        queryClient.setQueryData(changeRequestsQueries.jiraLinks(crId).queryKey, answer.links);
      }
      return answer.refreshed;
    },
    enabled: hasLinks,
    staleTime: JIRA_STATUS_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return links;
}

/**
 * CR-229: create or update the CR's parent Jira issue through the api-server.
 * An `existing_links` outcome wrote nothing, so it refreshes nothing either.
 */
export function useSendToJira(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<JiraSendOutcome, Error, Omit<JiraSendInput, "containerId" | "crId">>({
    mutationFn: (input) => sendToJira({ ...input, containerId: projectId, crId }),
    onSuccess: (outcome) => {
      if (outcome.status === "existing_links") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.jiraLinks(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}

/** CR-229 S6: sync the Technical Plan tasks as child issues of one parent link. */
export function useSyncJiraChildren(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<JiraChildrenSyncSummary, Error, { parentLinkId: string }>({
    mutationFn: ({ parentLinkId }) =>
      syncJiraChildren({ containerId: projectId, crId, parentLinkId }),
    // A partial sync still wrote links: refresh after a failure too.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.jiraLinks(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}

/** The server refreshes a Linear state when its snapshot is older than 4 min (S5). */
const LINEAR_STATE_STALE_MS = 4 * 60 * 1000;

/**
 * CR-239: the CR's Linear links. The database read renders first; then, when
 * the CR has links, the api-server is asked once per staleness window to
 * refresh their states from Linear, and a refreshed answer replaces the rows.
 * A failed refresh changes nothing (each link keeps its snapshot).
 */
export function useCrLinearLinks(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  const links = useQuery(changeRequestsQueries.linearLinks(crId));
  const hasLinks = (links.data?.length ?? 0) > 0;
  useQuery({
    queryKey: changeRequestsQueries.linearLinksRefreshKey(crId, projectId),
    queryFn: async () => {
      const answer = await fetchLinearLinks({ containerId: projectId, crId });
      if (answer.refreshed) {
        queryClient.setQueryData(changeRequestsQueries.linearLinks(crId).queryKey, answer.links);
      }
      return answer.refreshed;
    },
    enabled: hasLinks,
    staleTime: LINEAR_STATE_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return links;
}

/**
 * CR-239: create or update the CR's parent Linear issue through the api-server.
 * An `existing_links` outcome wrote nothing, so it refreshes nothing either.
 */
export function useSendToLinear(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<LinearSendOutcome, Error, Omit<LinearSendInput, "containerId" | "crId">>({
    mutationFn: (input) => sendToLinear({ ...input, containerId: projectId, crId }),
    onSuccess: (outcome) => {
      if (outcome.status === "existing_links") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linearLinks(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}

/** CR-239 S7: sync the Technical Plan tasks as sub-issues of one parent link. */
export function useSyncLinearChildren(crId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<LinearChildrenSyncSummary, Error, { parentLinkId: string }>({
    mutationFn: ({ parentLinkId }) =>
      syncLinearChildren({ containerId: projectId, crId, parentLinkId }),
    // A partial sync still wrote links: refresh after a failure too.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.linearLinks(crId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: changeRequestsQueries.events(crId).queryKey,
      });
    },
  });
}
